/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnChanges, OnInit,
} from '@angular/core';
import { FeedStoreService } from '../../services/feed-store.service';
import { toPreviewHtml } from '../../common/community/feed/preview-html';
import { ArticleService } from '../../services/article.service';
import { CommunityProfileCard, toTopicCard } from '../activity-card/community-profile-activity';
import type { PostsSegment } from '../community-profile.component';

const PINNED_FIRST = 'pinned_content,-published_at';

export const POSTS_SEGMENTS: { value: PostsSegment; label: string; state: string }[] = [
  { value: 'articles', label: 'Articles', state: 'profile.articles' },
  { value: 'status', label: 'Status updates', state: 'profile.status' },
  { value: 'topics', label: 'Forum posts', state: 'profile.topics' },
];

// The Articles, Status updates and Forum posts tabs (the profile.articles / status / topics states).
// Articles and statuses use the community feed card; on your own profile its Pin action pins one to the top.
@Component({
  selector: 'ut-community-profile-posts',
  templateUrl: './community-profile-posts.component.html',
  styleUrls: ['./community-profile-posts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunityProfilePostsComponent implements OnInit, OnChanges {
  @Input({ required: true }) user: any;
  @Input() me: any;
  @Input() isOwn = false;
  @Input() segment: PostsSegment = 'articles';

  stores: Record<PostsSegment, any>;
  topicCards: CommunityProfileCard[] = [];

  constructor(
    @Inject('StatusService') private StatusService: any,
    @Inject('UserService') private UserService: any,
    @Inject('$filter') private $filter: any,
    private articleService: ArticleService,
    private feedStore: FeedStoreService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const userId = this.user.id;
    this.stores = {
      articles: this.feedStore.create(
        (params) => this.articleService.feed(params),
        { sort: PINNED_FIRST, filter: { user_id: userId, state: 'published' } },
      ),
      status: this.feedStore.create((params) => this.StatusService.feed(params), { sort: PINNED_FIRST, filter: { user_id: userId } }),
      topics: this.feedStore.create((params) => this.UserService.topics(userId, params)),
    };
    this.ngOnChanges();
  }

  ngOnChanges(): void {
    if (this.stores) this.track(this.store.loadOnce());
  }

  get store(): any {
    return this.stores[this.segment];
  }

  get firstName(): string {
    return this.user.name?.split(' ')[0] || this.user.name;
  }

  // The card's Pin action saved; reload so the pinned item moves to the top.
  reload(): void {
    this.track(this.store.load());
  }

  remove(item: any): void {
    this.store.feedItems = this.store.feedItems.filter((i) => i !== item);
    this.cdr.markForCheck();
  }

  loadMore(): void {
    this.track(this.store.loadMore());
  }

  // FeedStore mutates in place, so re-render when a page starts and when it lands.
  private track(promise: any): void {
    this.cdr.markForCheck();
    promise.then(() => {
      if (this.segment === 'topics') this.buildTopicCards();
      this.cdr.markForCheck();
    });
  }

  private buildTopicCards(): void {
    const options = {
      owner: this.user,
      isOwn: this.isOwn,
      toText: (content: string) => toPreviewHtml(this.$filter('uMarkdown')(content || ''), this.$filter),
    };
    this.topicCards = this.store.feedItems.map((topic) => toTopicCard(topic, options));
  }
}
